import React, { useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from 'tss-react/mui';
import { Modal, Fade, Backdrop, Paper, Tooltip, Button } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckIcon from "@mui/icons-material/CheckOutlined";
import CallIcon from "@mui/icons-material/CallOutlined";
import EmailIcon from "@mui/icons-material/EmailOutlined";
import ContactAvatar from "./ContactAvatar";
import { formatUsPhone } from "../utils/contacts";

// Read-only contact card shared by the internal and external contact views.
// It shows only the details a contact actually has.

const useStyles = makeStyles()((theme) => ({
    modal: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: theme.spacing(2),
    },
    paper: {
        // Positioned so it stacks above the modal's backdrop; without this the
        // backdrop covers the card and swallows every click.
        position: "relative",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: 440,
        maxHeight: "calc(100vh - 32px)",
        borderRadius: 12,
        boxShadow: theme.shadows[8],
        outline: "none",
        overflow: "hidden",
    },
    header: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
        padding: theme.spacing(2.5, 1.5, 2, 3),
    },
    // The things people open a contact for: call it, write to it.
    actions: {
        display: "flex",
        gap: theme.spacing(1),
        padding: theme.spacing(0, 3, 2),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    action: {
        flex: 1,
        textTransform: "none",
        fontSize: 14,
        fontWeight: 500,
        borderRadius: 8,
        boxShadow: "none",
    },
    actionPrimary: {
        color: "#fff",
        backgroundColor: theme.palette.primary.light,
        "&:hover": {
            backgroundColor: theme.palette.primary.light,
            filter: "brightness(0.93)",
            boxShadow: "none",
        },
    },
    actionSecondary: {
        color: theme.palette.text.primary,
        borderColor: theme.palette.divider,
        "&:hover": {
            borderColor: theme.palette.text.secondary,
            backgroundColor: theme.palette.action.hover,
        },
    },
    headerDivider: {
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    heading: {
        flex: 1,
        minWidth: 0,
    },
    name: {
        fontSize: 20,
        fontWeight: 500,
        lineHeight: 1.3,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    subtitle: {
        fontSize: 13,
        lineHeight: 1.4,
        color: theme.palette.text.secondary,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    close: {
        alignSelf: "flex-start",
        color: theme.palette.text.secondary,
    },
    body: {
        overflowY: "auto",
        padding: theme.spacing(1, 0, 1.5),
    },
    sectionLabel: {
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: "0.06em",
        lineHeight: 1.4,
        textTransform: "uppercase",
        color: theme.palette.text.secondary,
        padding: theme.spacing(1.5, 3, 0.5),
    },
    row: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
        padding: theme.spacing(0.75, 1.5, 0.75, 3),
        "&:hover": {
            backgroundColor: theme.palette.action.hover,
        },
        // The copy button is faint until its row is hovered or focused.
        "&:hover .contact-card-copy, &:focus-within .contact-card-copy": {
            opacity: 1,
        },
    },
    rowIcon: {
        display: "flex",
        color: theme.palette.text.secondary,
        "& svg": {
            fontSize: 20,
        },
    },
    rowText: {
        flex: 1,
        minWidth: 0,
    },
    value: {
        fontSize: 15,
        lineHeight: 1.4,
        color: theme.palette.text.primary,
        wordBreak: "break-word",
        "& a": {
            color: theme.palette.primary.light,
            textDecoration: "none",
            "&:hover": {
                textDecoration: "underline",
            },
        },
    },
    label: {
        fontSize: 12,
        lineHeight: 1.4,
        color: theme.palette.text.secondary,
    },
    copy: {
        color: theme.palette.text.secondary,
        opacity: 0.55,
        transition: "opacity 0.15s ease",
    },
    rowCall: {
        color: theme.palette.primary.light,
    },
    empty: {
        padding: theme.spacing(3),
        textAlign: "center",
        fontSize: 14,
        color: theme.palette.text.secondary,
    },
}));

const hasValue = (value) => value !== null && value !== undefined && String(value).trim() !== "";

const ContactInfoCard = (props) => {
    const { classes, cx } = useStyles();
    const { open, onClose, name, subtitle, number, image, sections, onCall, callNumber, onEmail, email } = props;
    const [copied, setCopied] = useState(null);

    if (!open) return null;

    const copy = (key, text) => {
        const done = () => {
            setCopied(key);
            window.setTimeout(() => setCopied((current) => (current === key ? null : current)), 1500);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text).then(done).catch(() => {});
        }
    };

    const visibleSections = (sections || [])
        .map((section) => ({ ...section, rows: (section.rows || []).filter((row) => hasValue(row.value)) }))
        .filter((section) => section.rows.length > 0);

    const display = (row) => {
        const text = String(row.value).trim();
        if (row.type === "phone") return String(formatUsPhone(text));
        if (row.type === "email") return <a href={`mailto:${text}`}>{text}</a>;
        return text;
    };

    return (
        <Modal
            aria-labelledby="contact-card-name"
            className={classes.modal}
            open={open}
            onClose={onClose}
            closeAfterTransition
            slots={{
                backdrop: Backdrop
            }}
            slotProps={{
                backdrop: {
                    timeout: 300,
                }
            }}
        >
            <Fade in={open}>
                <Paper className={classes.paper}>
                    <div className={classes.header}>
                        <ContactAvatar name={name} number={number} image={image} size={56} />
                        <div className={classes.heading}>
                            <div id="contact-card-name" className={classes.name}>{name || "Contact"}</div>
                            {subtitle && <div className={classes.subtitle}>{subtitle}</div>}
                        </div>
                        <Tooltip title="Close">
                            <IconButton aria-label="close" className={classes.close} size="small" onClick={onClose}>
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </div>

                    {(hasValue(callNumber) && onCall) || (hasValue(email) && onEmail) ? (
                        <div className={classes.actions}>
                            {hasValue(callNumber) && onCall && (
                                <Button
                                    variant="contained"
                                    startIcon={<CallIcon />}
                                    className={cx(classes.action, classes.actionPrimary)}
                                    onClick={() => onCall(String(callNumber).trim())}
                                >
                                    Call
                                </Button>
                            )}
                            {hasValue(email) && onEmail && (
                                <Button
                                    variant="outlined"
                                    startIcon={<EmailIcon />}
                                    className={cx(classes.action, classes.actionSecondary)}
                                    onClick={() => onEmail(String(email).trim())}
                                >
                                    Email
                                </Button>
                            )}
                        </div>
                    ) : <div className={classes.headerDivider} />}

                    <div className={classes.body}>
                        {visibleSections.length === 0 && (
                            <div className={classes.empty}>No further details for this contact.</div>
                        )}
                        {visibleSections.map((section) => (
                            <div key={section.title}>
                                <div className={classes.sectionLabel}>{section.title}</div>
                                {section.rows.map((row) => {
                                    const key = `${section.title}:${row.label}`;
                                    const text = String(row.value).trim();
                                    return (
                                        <div key={key} className={classes.row}>
                                            <span className={classes.rowIcon}>{row.icon}</span>
                                            <div className={classes.rowText}>
                                                <div className={classes.value}>{display(row)}</div>
                                                <div className={classes.label}>{row.label}</div>
                                            </div>
                                            {row.type === "phone" && !row.noCall && onCall && (
                                                <Tooltip title="Call this number">
                                                    <IconButton
                                                        aria-label={`call ${row.label}`}
                                                        className={`${classes.copy} ${classes.rowCall} contact-card-copy`}
                                                        size="small"
                                                        onClick={() => onCall(text)}
                                                    >
                                                        <CallIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <Tooltip title={copied === key ? "Copied" : "Copy"}>
                                                <IconButton
                                                    aria-label={`copy ${row.label}`}
                                                    className={`${classes.copy} contact-card-copy`}
                                                    size="small"
                                                    onClick={() => copy(key, text)}
                                                >
                                                    {copied === key ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                                </IconButton>
                                            </Tooltip>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </Paper>
            </Fade>
        </Modal>
    );
};

ContactInfoCard.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    name: PropTypes.string,
    subtitle: PropTypes.string,
    sections: PropTypes.array,
    // Number the Call button dials, and what calling does.
    callNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onCall: PropTypes.func,
    email: PropTypes.string,
    onEmail: PropTypes.func,
};

export default ContactInfoCard;
